import {createSlice, createAsyncThunk} from '@reduxjs/toolkit'
import {
  getActiveStudentThunk,
  getActiveStudentIncThunk,
  getTotalExamParticipationThunk,
  getCourseExamParticipationThunk,
  getNumTotalExParThunk,
  getTotalActThunk,
  getPassedFailedStuThunk,
  getCountriesCountThunk,
  getStudyPeriodsThunk,
} from './studyPeriodThunk'

import {formatNumber} from '../../utils/axios'

// Constants
const WEEKS_COUNT = 13
const DEFAULT_Y_AXIS_MAX = 50000

// Common chart categories
const WEEK_CATEGORIES = [
  'Week 1', 'Week 2', 'Week 3', 'Month 1', 'Week 5', 'Week 6',
  'Week 7', 'Month 2', 'Week 9', 'Week 10', 'Week 11', 'Final', 'Supplementary'
]

const WEEK_CATEGORIES_SHORT = [
  'Week1', 'Week2', 'Week3', 'Monthly1', 'Week5', 'Week6',
  'Weekly7', 'Monthly2', 'Week9', 'Week10', 'Week11', 'Final', 'Supplementary'
]

// Helper functions
const safeParseFloat = (value, defaultValue = 0) => {
  const parsed = parseFloat(value)
  return isNaN(parsed) ? defaultValue : parsed
}

const safeParseInt = (value, defaultValue = 0) => {
  const parsed = parseInt(value, 10)
  return isNaN(parsed) ? defaultValue : parsed
}

const ensureArrayLength = (array, targetLength, defaultItem = {}) => {
  if (!array || array.length >= targetLength) return array || []
  
  const filledArray = [...array]
  while (filledArray.length < targetLength) {
    filledArray.push(typeof defaultItem === 'function' ? defaultItem() : defaultItem)
  }
  return filledArray
}

const createChartOptions = (config = {}) => ({
  chart: {
    toolbar: { show: true },
    ...config.chart,
  },
  dataLabels: { enabled: false, ...config.dataLabels },
  stroke: { show: false, width: 0, colors: ['transparent'], ...config.stroke },
  ...config,
})

// Study period filter helpers
const getFilteredPeriod = (state) => {
  return state.studyPeriodFilter.isFilterActive 
    ? state.studyPeriodFilter.selectedPeriod 
    : state.searchPeriod
}

const isValidPeriod = (period) => {
  return period && typeof period === 'number' && period > 0
}

// Chart configuration templates (functions not stored in state)
export const getPassedFailedChartOptions = () => ({
  chart: { type: 'donut' },
  labels: ['Passed', 'Failed'],
  colors: ['#38ada9', '#ee5253'],
  plotOptions: {
    pie: {
      startAngle: -90,
      endAngle: 90,
      offsetY: 10,
    },
  },
  grid: { padding: { bottom: -80 } },
  dataLabels: {
    formatter(val, opts) {
      const name = opts.w.globals.labels[opts.seriesIndex]
      return [name, val.toFixed(1) + '%']
    },
  },
  legend: { show: false, position: 'left' },
  responsive: [{
    breakpoint: 480,
    options: {
      chart: { width: 400 },
      legend: { position: 'bottom' },
    },
  }],
})

export const getActiveStudentChartOptions = () => createChartOptions({
  chart: { id: 'active-student-chart' },
  colors: ['#38ada9', '#C99B55'],
  plotOptions: {
    bar: {
      horizontal: false,
      borderRadius: 7,
      columnWidth: '30%',
      endingShape: 'rounded',
    },
  },
  xaxis: { categories: WEEK_CATEGORIES },
  responsive: [{
    breakpoint: 991,
    options: {
      plotOptions: {
        bar: {
          horizontal: true,
          borderRadius: 0,
          columnWidth: '30%',
        },
      },
    },
  }],
})

export const getActiveStudentIncChartOptions = () => createChartOptions({
  chart: { id: 'active-student-inc-chart' },
  colors: ['#C99B55', '#38ada9'],
  plotOptions: {
    bar: {
      horizontal: false,
      columnWidth: '70%',
      endingShape: 'rounded',
      borderRadius: 5,
    },
  },
  stroke: { width: 20 },
  xaxis: { categories: WEEK_CATEGORIES_SHORT },
})

export const getTotalExamParticipationOptions = () => ({
  chart: {
    height: 350,
    type: 'line',
    dropShadow: {
      enabled: true,
      color: '#000',
      top: 18,
      left: 7,
      blur: 10,
      opacity: 0.2,
    },
    toolbar: { show: false },
  },
  colors: ['#C99B55'],
  dataLabels: { enabled: true },
  stroke: { curve: 'smooth' },
  markers: { size: 1 },
  xaxis: { categories: WEEK_CATEGORIES },
})

export const getCourseExamParticipationOptions = () => ({
  chart: {
    height: 350,
    type: 'line',
    stacked: false,
    zoom: { enabled: false },
  },
  dataLabels: { enabled: true, enabledOnSeries: [2] },
  plotOptions: {
    bar: {
      horizontal: false,
      columnWidth: '30%',
      endingShape: 'rounded',
      borderRadius: 5,
    },
  },
  colors: ['#C99B55', '#38ada9', '#7E8299'],
  stroke: { width: [1, 1, 4] },
  fill: {
    opacity: [0.5, 1, 0.5],
    gradient: {
      inverseColors: false,
      shade: 'light',
      type: 'vertical',
      opacityFrom: 0.85,
      opacityTo: 0.55,
      stops: [0, 100, 100, 100],
    },
  },
  labels: [],
  xaxis: { categories: WEEK_CATEGORIES },
  yaxis: [
    {
      seriesName: 'Attempts Count',
      opposite: false,
      axisTicks: { show: true },
      axisBorder: { show: true, color: '#C99B55' },
      labels: { show: true, style: { colors: '#C99B55' } },
      min: 0,
      max: DEFAULT_Y_AXIS_MAX,
    },
    {
      seriesName: 'Users Count',
      opposite: false,
      axisTicks: { show: false },
      axisBorder: { show: true, color: '#38ada9' },
      labels: { style: { colors: '#38ada9' } },
      min: 0,
      max: DEFAULT_Y_AXIS_MAX,
    },
    {
      seriesName: 'Average Grade',
      opposite: true,
      axisTicks: { show: true },
      axisBorder: { show: true, color: '#FEB019' },
      labels: { style: { colors: '#FEB019' } },
    },
  ],
  tooltip: {
    fixed: {
      enabled: false,
      position: 'topLeft',
    },
  },
})

// Initial Data
const initialState = {
  studyPeriodNum: [],
  countryNum: 0,
  studyPeriodStartDate: 0,
  studyPeriodEndDate: 0,
  totalAverageGrade: 0,
  searchPeriod: 20,
  searchLang: 'ar',
  // selectedLanguage: 'ar',
  languageFilter: ['ar', 'en'],
  studyPeriodFilter: {
    selectedPeriod: null,
    availablePeriods: [],
    isFilterActive: false,
  },
  attemptsCount: 0,
  totalActiveStudent: 0,
  totalStudentLogin: 0,
  averageGrade: 0,
  averageParticipation: 0,
  // Active Student
  activeStudentData: {
    series: [
      { name: 'Active Students', data: [] },
      { name: 'Engaged Students', data: [] },
    ],
  },
  // Active Student Increase
  activeStudentDataInc: {
    series: [
      { name: 'Active Total', data: [] },
      { name: 'Logins Total', data: [] },
    ],
  },
  // Total Exam Participation
  TotalExamParticipation: {
    series: [{ name: 'Quiz Attempts', data: [] }],
  },
  //Passed Failed Exam
  PassedFailedStu: {
    series: [0, 0],
  },
  // Course Exam Participation
  courseExamParticipation: {
    series: [
      { name: 'Exam Participations', type: 'area', data: [] },
      { name: 'Active Students', type: 'column', data: [] },
      { name: 'Average Grade', type: 'line', data: [] },
    ],
    yAxisMax: {
      attempts: DEFAULT_Y_AXIS_MAX,
      users: DEFAULT_Y_AXIS_MAX,
    },
  },
}

// Active Student period
export const getStudyPeriods = createAsyncThunk(
  'activeStudent/getStudyPeriods',
  getStudyPeriodsThunk
)
// Active Student period
export const getCountriesCount = createAsyncThunk(
  'activeStudent/getCountriesCount',
  getCountriesCountThunk
)

// Active Student Chart
export const getActiveStudent = createAsyncThunk(
  'activeStudent/getActiveStudent',
  getActiveStudentThunk
)

// Active Total Student Chart
export const getTotalActiveStudent = createAsyncThunk(
  'activeStudent/getTotalActiveStudent',
  getTotalActThunk
)

// Active Student Increase Chart
export const getActiveStudentInc = createAsyncThunk(
  'activeStudent/getActiveStudentInc',
  getActiveStudentIncThunk
)

// Total Exame Participation
export const getTotalExamParticipation = createAsyncThunk(
  'activeStudent/getTotalExamParticipation',
  getTotalExamParticipationThunk
)

// Passed Failed Exam
export const getPassedFailedStu = createAsyncThunk(
  'activeStudent/getPassedFailedStu',
  getPassedFailedStuThunk
)

// Total Exam participation Number
export const getNumTotalExPar = createAsyncThunk(
  'activeStudent/getNumTotalExPar',
  getNumTotalExParThunk
)

// Course Exam Participation
export const getCourseExamParticipation = createAsyncThunk(
  'activeStudent/getCourseExamParticipation',
  getCourseExamParticipationThunk
)

const studyPeriodReducer = createSlice({
  name: 'studyPeriod',
  initialState,
  reducers: {
    handleChange: (state, {payload: {value}}) => {
      state.searchPeriod = value
    },
    handleChangeLang: (state, {payload: {value}}) => {
      state.searchLang = value
    },
    setStudyPeriodFilter: (state, {payload}) => {
      state.studyPeriodFilter.selectedPeriod = payload
      state.studyPeriodFilter.isFilterActive = payload !== null
      if (payload) {
        state.searchPeriod = payload
      }
    },
    clearStudyPeriodFilter: (state) => {
      state.studyPeriodFilter.selectedPeriod = null
      state.studyPeriodFilter.isFilterActive = false
    },
    updateAvailablePeriods: (state, {payload}) => {
      state.studyPeriodFilter.availablePeriods = payload || []
    },
    resetChartData: (state) => {
      state.activeStudentData.series.forEach(series => series.data = [])
      state.activeStudentDataInc.series.forEach(series => series.data = [])
      state.TotalExamParticipation.series.forEach(series => series.data = [])
      state.courseExamParticipation.series.forEach(series => series.data = [])
      state.PassedFailedStu.series = [0, 0]
    },
  },
  extraReducers: (builder) => {
    builder
      // Study Period Number
      .addCase(getStudyPeriods.pending, (state) => {
        state.isLoading = true
      })
      .addCase(getStudyPeriods.fulfilled, (state, {payload}) => {
        state.isLoading = false
        
        if (!payload?.length) {
          state.studyPeriodNum = []
          state.studyPeriodEndDate = 0
          state.studyPeriodStartDate = 0
          state.searchPeriod = 0
          state.studyPeriodFilter.availablePeriods = []
          state.studyPeriodFilter.selectedPeriod = null
          state.studyPeriodFilter.isFilterActive = false
          return
        }

        state.studyPeriodNum = payload.map((item) => item.number).slice(-3)

        // Update available periods for filter (hardcoded to periods 17-20)
        const hardcodedPeriods = []
        for (let i = 17; i <= 20; i++) {
          hardcodedPeriods.push({
            number: i,
            starting_date: Math.floor(Date.now() / 1000), // Current timestamp
            ending_date: Math.floor(Date.now() / 1000) + (365 * 24 * 60 * 60), // One year from now
            label: `Period ${i}`,
            isActive: true
          })
        }
        state.studyPeriodFilter.availablePeriods = hardcodedPeriods

        const now = Math.floor(Date.now() / 1000)
        const validPeriods = payload.filter(item => item.starting_date <= now)
        const latestPeriod = validPeriods[validPeriods.length - 1]

        if (latestPeriod) {
          state.studyPeriodEndDate = latestPeriod.ending_date
          state.studyPeriodStartDate = latestPeriod.starting_date
          state.searchPeriod = latestPeriod.number
          
          // Set default filter to latest period if no filter is active
          if (!state.studyPeriodFilter.isFilterActive) {
            state.studyPeriodFilter.selectedPeriod = latestPeriod.number
          }
        } else {
          state.studyPeriodEndDate = 0
          state.studyPeriodStartDate = 0
          state.searchPeriod = 0
          state.studyPeriodFilter.selectedPeriod = null
          state.studyPeriodFilter.isFilterActive = false
        }
      })
      .addCase(getStudyPeriods.rejected, (state, {payload}) => {
        state.isLoading = false
        console.error('Failed to fetch study periods:', payload)
      })

      // Countries Count
      .addCase(getCountriesCount.pending, (state) => {
        state.isLoading = true
      })
      .addCase(getCountriesCount.fulfilled, (state, {payload}) => {
        state.isLoading = false
        state.countryNum = payload?.[0]?.['COUNT(DISTINCT country)'] || 0
      })
      .addCase(getCountriesCount.rejected, (state, {payload}) => {
        state.isLoading = false
        console.error('Failed to fetch countries count:', payload)
      })

      // Active Student
      .addCase(getActiveStudent.pending, (state) => {
        state.isLoading = true
      })
      .addCase(getActiveStudent.fulfilled, (state, {payload}) => {
        state.isLoading = false
        const normalizedPayload = ensureArrayLength(payload || [], WEEKS_COUNT)

        state.activeStudentData.series[0].data = normalizedPayload.map((item) =>
          formatNumber(item?.active_total || 0)
        )
        state.activeStudentData.series[1].data = normalizedPayload.map((item) =>
          formatNumber(item?.enrolled_students_login || 0)
        )
      })
      .addCase(getActiveStudent.rejected, (state, {payload}) => {
        state.isLoading = false
        console.error('Failed to fetch active students:', payload)
      })

      // Total Active Students
      .addCase(getTotalActiveStudent.pending, (state) => {
        state.isLoading = true
      })
      .addCase(getTotalActiveStudent.fulfilled, (state, {payload}) => {
        state.isLoading = false

        if (payload?.length > 0) {
          const lastItem = payload[payload.length - 1]
          state.totalActiveStudent = lastItem?.active_total || 0
          state.totalStudentLogin = lastItem?.logins_total || 0
        } else {
          state.totalActiveStudent = 0
          state.totalStudentLogin = 0
        }
      })
      .addCase(getTotalActiveStudent.rejected, (state, {payload}) => {
        state.isLoading = false
        console.error('Failed to fetch total active students:', payload)
      })

      // Active Students Increase
      .addCase(getActiveStudentInc.pending, (state) => {
        state.isLoading = true
      })
      .addCase(getActiveStudentInc.fulfilled, (state, {payload}) => {
        state.isLoading = false
        const normalizedPayload = ensureArrayLength(payload || [], WEEKS_COUNT, () => ({
          active_total: 0,
          logins_total: 0
        }))

        state.activeStudentDataInc.series[0].data = normalizedPayload.map((item, index) => {
          if (index === 0) return item?.active_total || 0
          const currentValue = item?.active_total || 0
          const previousValue = normalizedPayload[index - 1]?.active_total || 0
          return Math.max(0, currentValue - previousValue)
        })

        state.activeStudentDataInc.series[1].data = normalizedPayload.map((item, index) => {
          if (index === 0) return item?.logins_total || 0
          const currentValue = item?.logins_total || 0
          const previousValue = normalizedPayload[index - 1]?.logins_total || 0
          return Math.max(0, currentValue - previousValue)
        })
      })
      .addCase(getActiveStudentInc.rejected, (state, {payload}) => {
        state.isLoading = false
        console.error('Failed to fetch active students increase:', payload)
      })

      // Total Exam Participation
      .addCase(getTotalExamParticipation.fulfilled, (state, {payload}) => {
        const normalizedPayload = ensureArrayLength(payload || [], WEEKS_COUNT, () => ({
          attempts_count: 0
        }))

        state.TotalExamParticipation.series[0].data = normalizedPayload.map((item) => 
          safeParseInt(item?.attempts_count, 0)
        )
      })
      .addCase(getTotalExamParticipation.rejected, (state, {payload}) => {
        console.error('Failed to fetch total exam participation:', payload)
      })

      // Passed Failed Students
      .addCase(getPassedFailedStu.fulfilled, (state, {payload}) => {
        if (payload?.[0]) {
          const data = payload[0]
          const passedCount = safeParseFloat(data.passed_attempts_count)
          const failedCount = safeParseFloat(data.failed_attempts_count)
          state.PassedFailedStu.series = [passedCount, failedCount]
          state.totalAverageGrade = safeParseFloat(data.average_grade).toFixed(2)
        } else {
          state.PassedFailedStu.series = [0, 0]
          state.totalAverageGrade = '0.00'
        }
      })
      .addCase(getPassedFailedStu.rejected, (state, {payload}) => {
        console.error('Failed to fetch passed/failed students:', payload)
      })

      // Total Exam Participation Number
      .addCase(getNumTotalExPar.pending, (state) => {
        state.isLoading = true
      })
      .addCase(getNumTotalExPar.fulfilled, (state, {payload}) => {
        state.isLoading = false
        state.attemptsCount = payload?.[0]?.attempts_count || 0
      })
      .addCase(getNumTotalExPar.rejected, (state, {payload}) => {
        state.isLoading = false
        console.error('Failed to fetch total exam participation number:', payload)
      })

      // Course Exam Participation
      .addCase(getCourseExamParticipation.pending, (state) => {
        state.isLoading = true
      })
      .addCase(getCourseExamParticipation.fulfilled, (state, {payload}) => {
        state.isLoading = false
        const normalizedPayload = ensureArrayLength(payload || [], WEEKS_COUNT, () => ({
          attempts_count: 0,
          average_duration: 0,
          average_grade: 0,
          exam_id: '',
          failed_attempts_count: 0,
          female_attempts_count: 0,
          female_students_count: 0,
          male_attempts_count: 0,
          male_students_count: 0,
          passed_attempts_count: 0,
          users_count: 0,
        }))

        // Update series data
        state.courseExamParticipation.series[0].data = normalizedPayload.map(item => 
          safeParseInt(item?.attempts_count)
        )
        state.courseExamParticipation.series[1].data = normalizedPayload.map(item => 
          safeParseInt(item?.users_count)
        )
        state.courseExamParticipation.series[2].data = normalizedPayload.map(item => 
          safeParseFloat(item?.average_grade).toFixed(0)
        )

        // Calculate dynamic Y-axis max values
        const attemptsValues = normalizedPayload.map(item => safeParseInt(item?.attempts_count))
        const usersValues = normalizedPayload.map(item => safeParseInt(item?.users_count))

        state.courseExamParticipation.yAxisMax.attempts = 
          Math.ceil(Math.max(...attemptsValues, 1000) / 1000) * 1000

        state.courseExamParticipation.yAxisMax.users = 
          Math.ceil(Math.max(...usersValues, 1000) / 1000) * 1000

        // Calculate averages
        const totalAttempts = attemptsValues.reduce((sum, val) => sum + val, 0)
        const weightedGradeSum = normalizedPayload.reduce((sum, item) => 
          sum + (safeParseFloat(item?.average_grade) * safeParseInt(item?.attempts_count)), 0
        )

        state.averageGrade = totalAttempts > 0 ? (weightedGradeSum / totalAttempts).toFixed(2) : '0.00'
        state.averageParticipation = normalizedPayload.length > 0 ? 
          (totalAttempts / normalizedPayload.length).toFixed(2) : '0.00'
      })
      .addCase(getCourseExamParticipation.rejected, (state, {payload}) => {
        state.isLoading = false
        console.error('Failed to fetch course exam participation:', payload)
      })
  },
})

export const {
  handleChange, 
  handleChangeLang, 
  resetChartData,
  setStudyPeriodFilter,
  clearStudyPeriodFilter,
  updateAvailablePeriods
} = studyPeriodReducer.actions

// Selectors for study period filter
export const selectStudyPeriodFilter = (state) => state.studyPeriod.studyPeriodFilter
export const selectAvailablePeriods = (state) => state.studyPeriod.studyPeriodFilter.availablePeriods
export const selectSelectedPeriod = (state) => state.studyPeriod.studyPeriodFilter.selectedPeriod
export const selectIsFilterActive = (state) => state.studyPeriod.studyPeriodFilter.isFilterActive
export const selectCurrentPeriod = (state) => getFilteredPeriod(state.studyPeriod)

// Selectors for chart data and options
export const selectActiveStudentData = (state) => state.studyPeriod.activeStudentData
export const selectActiveStudentIncData = (state) => state.studyPeriod.activeStudentDataInc
export const selectTotalExamParticipation = (state) => state.studyPeriod.TotalExamParticipation
export const selectPassedFailedStu = (state) => state.studyPeriod.PassedFailedStu
export const selectCourseExamParticipation = (state) => state.studyPeriod.courseExamParticipation

// Selector to get course exam participation options with dynamic Y-axis
export const selectCourseExamParticipationOptions = (state) => {
  const options = getCourseExamParticipationOptions()
  const { yAxisMax } = state.studyPeriod.courseExamParticipation
  
  options.yaxis[0].max = yAxisMax.attempts
  options.yaxis[1].max = yAxisMax.users
  
  return options
}

export default studyPeriodReducer.reducer
